import React from 'react';
import { useSelector } from 'react-redux';
import { generateEventSourceByPageType } from '../../common/analytics/helpers/analyticsSourceHelper';
import {
  trackArticle,
  trackArticleFeedShowMore,
  trackArticleFeedTab,
  trackHeroArticle,
  trackHeroVideoArticle,
  trackMostPopularArticle,
  trackViewMoreLink,
} from '../../common/analytics/analyticsTracker';
import { ANALYTICS_FEATURES } from '../../common/analytics/featureTypes';
import config from '../../common/config';
import { AdUnits } from '../../common/helpers/ads/dfp';
import { fetchDecorLandingPageData } from '../../common/helpers/data/landingPages/fetchNewsGuidesDecorLandingPageData';
import { Content } from '../../common/helpers/data/landingPages/fetchPostsData';
import { fetchLandingPageTopContentData } from '../../common/helpers/data/landingPages/fetchTopContentData';
import {
  getLandingPageType,
  LandingPageTypes,
} from '../../common/helpers/domain/landingPages';
import {
  getSubcategories,
  PostMainCategory,
  PostSubcategory,
} from '../../common/helpers/domain/postCategories';
import logger from '../../common/helpers/logger/logger';
import { extractPostIds } from '../../components/articleFeed/articleFeedHelper';
import { getDecorTabDefinition } from '../../components/articleFeed/articleFeedTab';
import { Breadcrumb } from '../../components/global/breadcrumb/Breadcrumb';
import { PostList } from '../../components/global/PostList/PostList';
import HeroArticle from '../../components/homePage/heroArticle/HeroArticle';
import HomeSeoHeader from '../../components/homePage/homeSeoHeader/HomeSeoHeader';
import { ModuleTitle } from '../../components/homePage/moduleTitle/ModuleTitle';
import { generatePostMainCategoryBreadcrumbList } from '../../components/landingPage/breadcrumb';
import PostListModule from '../../components/landingPage/PostListModule';
import BillboardAd from '../../components/landingPageAds/BillboardAd';
import MainTitle from '../../components/mainTitle/MainTitle';
import MostPopularPosts from '../../components/mostPopular/MostPopularPosts';
import {
  DynamicComponentForArticleFeedModule,
  DynamicComponentForFeaturedVideos,
  DynamicComponentForHeroVideo,
} from '../../components/pages/homePage/DynamicComponents';
import {
  AsideContainer,
  LeftContainer,
  TopContentWrapper,
} from '../../components/pages/homePage/HomePage.style';
import { homePageRowSetting } from '../../components/rows/homePageRowSetting';
import Row from '../../components/rows/Row';
import SeoFooter from '../../components/seoFooter/SeoFooter';
import ViewMoreLink from '../../components/ViewMoreLink/ViewMoreLink';
import Page from '../../layouts/main';
import { CustomNextPageContext } from '../../lib/customNextPageContext';
import commonResource from '../../locales/th/common.json';
import homeResource from '../../locales/th/home.json';
import { HeroArticleInfo } from '../../queries/homePage/heroArticles';
import { HeroVideoInfo } from '../../queries/homePage/heroVideo';
import { MostPopular } from '../../queries/homePage/mostPopular';
import { updatePageType } from '../../store/home/slice';
import { updateCurrentSourcePage } from '../../store/search/slice';
import { WholeState } from '../../store/common/type';
import { getAdSectByPageType } from '../../common/helpers/ads/AdsHelper';

interface DecorLandingPageProps {
  canonicalLink: string;
  heroArticle: HeroArticleInfo;
  popularArticles: MostPopular[];
  interiorInspirationPosts: Content[];
  renovationPosts: Content[];
  maintenancePosts: Content[];
  heroVideo: HeroVideoInfo;
  featuredVideos: Content[];
}

function DecorLandingPage({
  canonicalLink,
  heroArticle,
  popularArticles,
  interiorInspirationPosts,
  renovationPosts,
  maintenancePosts,
  heroVideo,
  featuredVideos,
}: DecorLandingPageProps) {
  const moduleResource = commonResource.postModules.decor;
  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );
  const analyticsSource = generateEventSourceByPageType(
    LandingPageTypes.DecorsPage,
  );

  return (
    <Page
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      // @ts-ignore
      source={analyticsSource}
    >
      <HomeSeoHeader
        pageType={LandingPageTypes.DecorsPage}
        canonicalLink={canonicalLink}
      />
      <Breadcrumb
        items={generatePostMainCategoryBreadcrumbList(PostMainCategory.Decor)}
      />
      <Row verticalSpacing={0} maxWidth={homePageRowSetting.maxWidth}>
        <MainTitle
          title={homeResource.title.decor}
          subtitle={homeResource.subtitle.decor}
        />
      </Row>
      <Row
        verticalSpacing={0}
        maxWidth={homePageRowSetting.maxWidth}
        fullWidth={{ desktop: false, mobile: true, tablet: true }}
      >
        <TopContentWrapper>
          <LeftContainer>
            <HeroArticle
              heroArticleData={heroArticle}
              onClickHeroArticle={(heroArticle) =>
                trackHeroArticle(
                  userInfo,
                  heroArticle,
                  analyticsSource,
                  ANALYTICS_FEATURES.HERO_ARTICLE,
                )
              }
            />
          </LeftContainer>
          <AsideContainer>
            <MostPopularPosts
              post={popularArticles}
              onClick={(popularArticle) =>
                trackMostPopularArticle(
                  userInfo,
                  popularArticle,
                  analyticsSource,
                  ANALYTICS_FEATURES.POPULAR,
                )
              }
            />
          </AsideContainer>
        </TopContentWrapper>
      </Row>
      <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
        <BillboardAd
          adUnit={AdUnits.HP_Billboard1}
          sect={getAdSectByPageType(LandingPageTypes.DecorsPage)}
        />
      </Row>
      {interiorInspirationPosts.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="interior-inspiration"
        >
          <PostListModule
            title={moduleResource.interiorInspiration.title}
            subtitle={moduleResource.interiorInspiration.subtitle}
            viewMoreLink={
              <ViewMoreLink
                landingPageType={getLandingPageType([
                  PostSubcategory.InteriorInspiration,
                ])}
                onClick={(targetLandingPage) => {
                  trackViewMoreLink(
                    userInfo,
                    analyticsSource,
                    targetLandingPage,
                    ANALYTICS_FEATURES.LATEST_INTERIOR_INSPIRATION,
                  );
                }}
              />
            }
          >
            <PostList
              contents={interiorInspirationPosts}
              adUnit={AdUnits.HP_Pos1}
              adSect={getAdSectByPageType(LandingPageTypes.DecorsPage)}
              onClickPostCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.LATEST_INTERIOR_INSPIRATION,
                )
              }
            />
          </PostListModule>
        </Row>
      )}
      {(heroVideo || featuredVideos.length > 0) && (
        <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
          <div style={{ overflowX: 'hidden' }}>
            <DynamicComponentForHeroVideo
              heroVideo={heroVideo}
              onClick={(heroVideo) =>
                trackHeroVideoArticle(
                  userInfo,
                  heroVideo,
                  analyticsSource,
                  ANALYTICS_FEATURES.HERO_VIDEO,
                )
              }
            />
            <DynamicComponentForFeaturedVideos
              contents={featuredVideos}
              onClickVideoCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.VIDEO,
                )
              }
            />
          </div>
        </Row>
      )}
      {renovationPosts.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="renovation"
        >
          <PostListModule
            title={moduleResource.renovation.title}
            subtitle={moduleResource.renovation.subtitle}
            viewMoreLink={
              <ViewMoreLink
                landingPageType={getLandingPageType([
                  PostSubcategory.Renovation,
                ])}
                onClick={(targetLandingPage) => {
                  trackViewMoreLink(
                    userInfo,
                    analyticsSource,
                    targetLandingPage,
                    ANALYTICS_FEATURES.LATEST_RENOVATION,
                  );
                }}
              />
            }
          >
            <PostList
              contents={renovationPosts}
              adUnit={AdUnits.HP_Pos2}
              adSect={getAdSectByPageType(LandingPageTypes.DecorsPage)}
              onClickPostCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.LATEST_RENOVATION,
                )
              }
            />
          </PostListModule>
        </Row>
      )}
      {maintenancePosts.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="maintenance"
        >
          <PostListModule
            title={moduleResource.maintenance.title}
            subtitle={moduleResource.maintenance.subtitle}
            viewMoreLink={
              <ViewMoreLink
                landingPageType={getLandingPageType([
                  PostSubcategory.Maintenance,
                ])}
                onClick={(targetLandingPage) => {
                  trackViewMoreLink(
                    userInfo,
                    analyticsSource,
                    targetLandingPage,
                    ANALYTICS_FEATURES.LATEST_MAINTENANCE,
                  );
                }}
              />
            }
          >
            <PostList
              contents={maintenancePosts}
              adUnit={AdUnits.HP_Pos3}
              adSect={getAdSectByPageType(LandingPageTypes.DecorsPage)}
              onClickPostCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.LATEST_MAINTENANCE,
                )
              }
            />
          </PostListModule>
        </Row>
      )}
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
      >
        <ModuleTitle title={homeResource.readMore.title} />
        <DynamicComponentForArticleFeedModule
          tabDefinition={getDecorTabDefinition()}
          excludePostIds={extractPostIds(
            [
              ...interiorInspirationPosts,
              ...renovationPosts,
              ...maintenancePosts,
              ...featuredVideos,
            ],
            heroVideo,
          )}
          adUnit={AdUnits.HP_MrecInfiniteAd}
          adSect={getAdSectByPageType(LandingPageTypes.DecorsPage)}
          onClickPostCard={(post) =>
            trackArticle(
              userInfo,
              post,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            )
          }
          onClickArticleFeedShowMore={() => {
            trackArticleFeedShowMore(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            );
          }}
          onClickArticleFeedTab={(selectedTab) => {
            trackArticleFeedTab(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
              selectedTab,
            );
          }}
        />
      </Row>
      <Row verticalSpacing={16} fullWidth>
        <SeoFooter />
      </Row>
    </Page>
  );
}

DecorLandingPage.getInitialProps = async ({
  store,
  asPath,
  apolloClient,
}: CustomNextPageContext): Promise<DecorLandingPageProps> => {
  const canonicalLink = config.baseUrl + asPath.substring(1);
  try {
    const { heroArticle, mostPopular } = await fetchLandingPageTopContentData(
      apolloClient,
      {
        postCategories: getSubcategories(PostMainCategory.Decor),
      },
    );

    const { interiorInspiration, renovation, maintenance, video } =
      await fetchDecorLandingPageData(
        apolloClient,
        heroArticle?.id ? [heroArticle.id] : [],
      );

    const landingPageType = LandingPageTypes.DecorsPage;
    store.dispatch(updatePageType(landingPageType));
    store.dispatch(
      updateCurrentSourcePage(generateEventSourceByPageType(landingPageType)),
    );

    return {
      canonicalLink,
      heroArticle,
      popularArticles: mostPopular,
      interiorInspirationPosts: interiorInspiration,
      renovationPosts: renovation,
      maintenancePosts: maintenance,
      heroVideo: video.heroVideo,
      featuredVideos: video.featuredVideos,
    };
  } catch (e) {
    logger.logError(
      'DecorLandingPage.getInitialProps',
      'Error fetching data',
      e,
    );
  }
};

export default DecorLandingPage;
